import type { OutboxItem } from '../domain/outbox-item.js';
import { toProvisionalCode } from '../domain/provisional-code.js';
import type { OfflineDb } from '../infrastructure/dexie/offline-db.js';
import type { DexieBlobStore } from '../infrastructure/dexie-blob-store.js';
import type { DexieOutboxRepository } from '../infrastructure/dexie-outbox.repository.js';

export interface EnqueueCheckinInput {
  /** UUID, one per form session (CheckinPage — Slice 1's own clientRequestId lifecycle). */
  clientRequestId: string;
  orgId: string;
  /** Claimed operator identity — design D12 (audit only; the syncing operator may differ). */
  capturedByUserId: string;
  locationId: string;
  /** Everything CreateTicketRequest needs except photoKeys (uploaded at sync, Slice 7). */
  payload: Record<string, unknown>;
  /** Local blob ids captured for this item (DexieBlobStore rows), in angle order. */
  photoRefs: string[];
  /**
   * Third adversarial review finding H3 — local blob ids for the optional
   * incident photos, in capture order. Optional (defaults to `[]`) —
   * backward compatible with every caller/test that predates incident
   * photos going through the outbox.
   */
  incidentPhotoRefs?: string[];
  /** Injectable for tests — defaults to `new Date()`. */
  now?: Date;
}

/**
 * enqueueCheckin (design D5/D12, task 6.4) — the ONLY way a check-in enters
 * the outbox. Writes one row, state `pending`, and returns as soon as the
 * Dexie transaction has committed — no network call here, online or
 * offline. This is the literal implementation of "outbox-always": a caller
 * (CheckinPage) can show the operator an immediate, durable confirmation the
 * instant this promise resolves, without waiting on the 4 photo uploads or
 * any server round trip (binding requirement — never block on photo
 * uploads).
 */
export async function enqueueCheckin(
  repo: DexieOutboxRepository,
  input: EnqueueCheckinInput,
  /**
   * Optional — second adversarial review BLOCKER #1. When provided, every
   * blob in `input.photoRefs` is re-linked (its `outboxId` FIELD updated,
   * never its own id/content) to THIS item's id. Covers the failed-enqueue-
   * then-edit scenario: CheckinPage mints a NEW `clientRequestId` when the
   * form fingerprint changed since the last submit attempt, but the
   * already-captured blobs still carry the OLD id in their `outboxId`
   * field — without this, `cleanupSyncedBlobs`'s (Slice 4) own
   * outboxId-based grouping would never find them under the new item.
   * Omitted in most existing tests (backward compatible — no relink is a
   * safe no-op, since the handler itself loads blobs by explicit
   * `photoRefs` id, never by outboxId grouping).
   */
  blobStore?: Pick<DexieBlobStore, 'relinkToOutbox'>,
  /**
   * Optional — fourth adversarial review, LOW #4. When provided together
   * with `blobStore`, wraps `repo.put()` + `blobStore.relinkToOutbox()` in
   * a SINGLE Dexie transaction spanning both the `outbox` and `blobs`
   * tables, so a crash between the two writes can never leave blobs still
   * tagged with the OLD outboxId while the NEW item is already durably
   * enqueued (or vice versa). Omitted — the previous, non-atomic two-step
   * behavior — for backward compatibility with existing tests/callers that
   * don't pass it.
   */
  db?: Pick<OfflineDb, 'transaction' | 'outbox' | 'blobs'>,
): Promise<OutboxItem> {
  const now = input.now ?? new Date();
  const nowIso = now.toISOString();
  const seq = await repo.nextSeq();

  const item: OutboxItem = {
    id: input.clientRequestId,
    kind: 'checkin.create',
    state: 'pending',
    orgId: input.orgId,
    capturedByUserId: input.capturedByUserId,
    locationId: input.locationId,
    // Fixed HERE, once — task 6.1's forward note (Slice 5): never recompute
    // this on a later sync attempt, or a lost-response retry breaks Slice
    // 1's idempotency guarantee (see outbox-item.ts's own doc comment).
    capturedAt: nowIso,
    provisionalCode: toProvisionalCode(input.clientRequestId),
    payload: input.payload,
    photoRefs: input.photoRefs,
    incidentPhotoRefs: input.incidentPhotoRefs ?? [],
    seq,
    attempts: 0,
    nextAttemptAt: null,
    lastError: null,
    result: null,
    createdAt: nowIso,
    updatedAt: nowIso,
  };

  // Durable-before-return — mirrors DexieBlobStore's own contract (Slice 4)
  // and the binding requirement "enqueue must be durable: commit before
  // showing success". `repo.put()` resolves only once Dexie's underlying
  // IndexedDB transaction has committed.
  const allRefs = [...input.photoRefs, ...(input.incidentPhotoRefs ?? [])];
  const shouldRelink = blobStore && allRefs.length > 0;

  if (db) {
    // Fourth adversarial review, LOW #4 — ATOMIC: both writes commit or
    // roll back together. Dexie automatically scopes any table operation
    // performed synchronously within this callback (even through the
    // `repo`/`blobStore` wrapper classes) into THIS transaction, as long as
    // they operate on tables of the SAME `db` instance passed here.
    await db.transaction('rw', db.outbox, db.blobs, async () => {
      await repo.put(item);
      if (shouldRelink) {
        await blobStore!.relinkToOutbox(allRefs, item.id);
      }
    });
  } else {
    await repo.put(item);
    if (shouldRelink) {
      await blobStore!.relinkToOutbox(allRefs, item.id);
    }
  }

  return item;
}
